// ═══════════════════════════════════════════════════════════════
// Business Owner Portal — Dashboard
// Org overview, alerts, quick stats, pending checks
// ═══════════════════════════════════════════════════════════════

import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Title, Text, Paper, Group, Badge, SimpleGrid, Button, RingProgress,
  Stack, ThemeIcon,
} from '../../../components/gab/GabComponents';
import {
  IconBuilding, IconBuildingWarehouse, IconAddressBook,
  IconAlertTriangle, IconPackage, IconShieldCheck,
  IconClipboardCheck,
} from '@tabler/icons-react';
import { useMyOrg } from '../../../hooks/useMyOrg';
import {
  useMyFacilities, useMyContacts, useMyDamageReports,
  useMyResourceOffers, useMyReentryPasses, useMyStatusResponses,
} from '../../../hooks/useMyOrgData';
import { useMainStreetOpsStore } from '../../../store/mainstreetOpsStore';
import { useAuthStore } from '../../../store/authStore';
import { SECTOR_TYPES, OP_STATUS } from '../constants';

function StatCard({ icon: Icon, label, value, color, onClick }: {
  icon: React.ComponentType<{ size: number }>;
  label: string;
  value: number;
  color: string;
  onClick?: () => void;
}) {
  return (
    <Paper withBorder p="md" style={{ cursor: onClick ? 'pointer' : undefined }} onClick={onClick}>
      <Group gap="sm">
        <ThemeIcon size="lg" variant="light" color={color}>
          <Icon size={18} />
        </ThemeIcon>
        <div>
          <Text size="xl" fw={700}>{value}</Text>
          <Text size="xs" c="dimmed">{label}</Text>
        </div>
      </Group>
    </Paper>
  );
}

export default function PortalDashboard() {
  const navigate = useNavigate();
  const myOrg = useMyOrg();
  const activeIncident = useAuthStore(s => s.activeIncident);
  const currentUser = useAuthStore(s => s.user);
  const myFacilities = useMyFacilities();
  const myContacts = useMyContacts();
  const myDamageReports = useMyDamageReports();
  const myResourceOffers = useMyResourceOffers();
  const myReentryPasses = useMyReentryPasses();
  const myResponses = useMyStatusResponses();
  const statusChecks = useMainStreetOpsStore(s => s.statusChecks);
  const posts = useMainStreetOpsStore(s => s.posts);

  const sectorLabel = SECTOR_TYPES.find(s => s.id === myOrg?.sectorType)?.label ?? myOrg?.sectorType ?? '';
  const opStatus = OP_STATUS.find(s => s.id === myOrg?.opStatus);

  // Status checks targeting this org
  const pendingChecks = useMemo(() => {
    if (!myOrg) return [];
    const orgId = String(myOrg.id);
    const sector = myOrg.sectorType ?? '';
    const respondedCheckIds = new Set(myResponses.map(r => r.statusCheckId));

    return statusChecks.filter(sc => {
      if (sc.status !== 'open') return false;
      if (activeIncident && String(sc.incidentId) !== String(activeIncident.id)) return false;
      // Already responded
      if (respondedCheckIds.has(String(sc.id))) return false;
      // Targeting logic
      if (sc.targetType === 'all') return true;
      if (sc.targetType === 'sector' && sc.targetSectors.split(',').includes(sector)) return true;
      if (sc.targetType === 'specific' && sc.targetOrgs.split(',').includes(orgId)) return true;
      return false;
    });
  }, [statusChecks, myOrg, myResponses, activeIncident]);

  // Recent posts for current incident
  const recentPosts = useMemo(() => {
    let list = posts;
    if (activeIncident) {
      list = list.filter(p => String(p.incidentId) === String(activeIncident.id));
    }
    return [...list]
      .sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? ''))
      .slice(0, 5);
  }, [posts, activeIncident]);

  // Profile completeness
  const profileFields = [
    myOrg?.name, myOrg?.sectorType, myOrg?.address, myOrg?.phone,
    myOrg?.email, myOrg?.primaryContact, myOrg?.opStatus,
  ];
  const filledCount = profileFields.filter(Boolean).length;
  const completeness = Math.round((filledCount / profileFields.length) * 100);

  if (!myOrg) {
    return (
      <Paper p="xl" withBorder>
        <Stack align="center" gap="sm">
          <ThemeIcon size={60} radius="xl" variant="light" color="#F86A0B">
            <IconAlertTriangle size={30} />
          </ThemeIcon>
          <Title order={4} c="black">Business Profile Not Found</Title>
          <Text size="sm" c="dimmed" ta="center" maw={460}>
            We couldn't find a business linked to your email address ({currentUser?.email || 'unknown'}).
            Ask your admin to add you to your business's contact list, or register your business
            below if it isn't already in the system.
          </Text>
          <Group mt="sm">
            <Button variant="light" onClick={() => navigate('/mainstreet/register')}>
              Register a Business
            </Button>
            <Button variant="subtle" onClick={() => navigate('/')}>
              Back to Home
            </Button>
          </Group>
        </Stack>
      </Paper>
    );
  }

  return (
    <div>
      <Group justify="space-between" mb="md">
        <div>
          <Title order={3}>{myOrg.name}</Title>
          <Group gap="xs" mt={4}>
            {sectorLabel && <Badge size="sm" variant="light">{sectorLabel}</Badge>}
            {opStatus && <Badge size="sm" color={opStatus.color}>{opStatus.label}</Badge>}
          </Group>
        </div>
        <Button size="xs" variant="light" leftSection={<IconBuilding size={14} />}
          onClick={() => navigate('/mainstreet/my-business')}>
          Edit Profile
        </Button>
      </Group>

      {/* Profile Completeness */}
      {completeness < 100 && (
        <Paper withBorder p="md" mb="md" bg="rgba(198,135,0,0.08)">
          <Group gap="md">
            <RingProgress size={60} thickness={6} sections={[{ value: completeness, color: '#C68700' }]}
              label={<Text ta="center" size="xs" fw={700}>{completeness}%</Text>} />
            <div>
              <Text size="sm" fw={600}>Complete Your Profile</Text>
              <Text size="xs" c="dimmed">
                Fill in missing organization details to help EOC staff serve you better.
              </Text>
            </div>
          </Group>
        </Paper>
      )}

      {/* Pending Checks Alert */}
      {pendingChecks.length > 0 && (
        <Paper withBorder p="md" mb="md" bg="rgba(75,63,255,0.08)"
          style={{ borderLeft: '4px solid var(--color-primary-700)', cursor: 'pointer' }}
          onClick={() => navigate('/mainstreet/status')}>
          <Group gap="xs">
            <IconClipboardCheck size={18} color="var(--color-primary-700)" />
            <Text size="sm" fw={600} c="var(--color-primary-700)">
              {pendingChecks.length} pending status check{pendingChecks.length !== 1 ? 's' : ''} — click to respond
            </Text>
          </Group>
        </Paper>
      )}

      {/* Stats Grid */}
      <SimpleGrid cols={{ base: 2, sm: 3, md: 4 }} spacing="sm" mb="lg">
        <StatCard icon={IconBuildingWarehouse} label="Facilities" value={myFacilities.length}
          color="var(--color-primary-700)" onClick={() => navigate('/mainstreet/facilities')} />
        <StatCard icon={IconAddressBook} label="Contacts" value={myContacts.length}
          color="var(--data-viz-650)" onClick={() => navigate('/mainstreet/contacts')} />
        <StatCard icon={IconAlertTriangle} label="Damage Reports" value={myDamageReports.length}
          color="#F86A0B" onClick={() => navigate('/mainstreet/damage')} />
        <StatCard icon={IconPackage} label="Resource Offers" value={myResourceOffers.length}
          color="#03AC9E" onClick={() => navigate('/mainstreet/resources')} />
        <StatCard icon={IconShieldCheck} label="Re-entry Passes" value={myReentryPasses.length}
          color="var(--color-success)" onClick={() => navigate('/mainstreet/reentry')} />
        <StatCard icon={IconClipboardCheck} label="Responses" value={myResponses.length}
          color="#03AC9E" onClick={() => navigate('/mainstreet/status')} />
      </SimpleGrid>

      {/* Recent Feed */}
      {recentPosts.length > 0 && (
        <>
          <Group justify="space-between" mb="xs">
            <Title order={4}>Recent Feed</Title>
            <Button size="xs" variant="subtle" onClick={() => navigate('/mainstreet/feed')}>
              View All
            </Button>
          </Group>
          <Stack gap="xs">
            {recentPosts.map(post => (
              <Paper key={post.id} p="sm" withBorder>
                <Group gap="xs" mb={4}>
                  <Text size="sm" fw={600}>{post.authorName}</Text>
                  <Badge size="xs" color={
                    post.postType === 'alert' ? 'var(--color-error)' :
                    post.postType === 'announcement' ? 'var(--color-primary-700)' :
                    post.postType === 'question' ? 'var(--data-viz-650)' : '#03AC9E'
                  }>{post.postType}</Badge>
                  <Text size="xs" c="dimmed">
                    {post.createdAt ? new Date(post.createdAt).toLocaleDateString() : ''}
                  </Text>
                </Group>
                <Text size="sm" lineClamp={2}>{post.content}</Text>
              </Paper>
            ))}
          </Stack>
        </>
      )}
    </div>
  );
}
